import { useState, useEffect, useRef } from 'react';
import { BillService } from '@/services/invoiceService';
import { Invoice } from '@/models/invoice';

interface UseInvoiceDataResult {
  invoice: Invoice | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useInvoiceData = (id: string | undefined): UseInvoiceDataResult => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  const fetchInvoice = async () => {
    if (!id) {
      setError('Invoice ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await BillService.getBillById(id);
      
      if (response.success && response.data) {
        setInvoice(response.data);
        retryCountRef.current = 0; // Reset retry count on success
      } else {
        setError(response.message || 'Failed to load invoice');
      }
    } catch (err: any) {
      console.error('Error fetching invoice:', err);
      
      // Handle specific error cases
      if (err.message === 'Duplicate request cancelled' && retryCountRef.current < maxRetries) {
        // This is a duplicate request error, wait a bit and retry without duplicate check
        console.log(`Duplicate request detected, retrying without duplicate check... (attempt ${retryCountRef.current + 1})`);
        retryCountRef.current++;
        
        setTimeout(async () => {
          try {
            setLoading(true);
            setError(null);
            
            const response = await BillService.getBillByIdWithoutDuplicateCheck(id);
            
            if (response.success && response.data) {
              setInvoice(response.data);
              retryCountRef.current = 0; // Reset retry count on success
            } else {
              setError(response.message || 'Failed to load invoice');
            }
          } catch (retryErr: any) {
            console.error('Retry failed:', retryErr);
            setError('Failed to load invoice after retry. Please try again.');
          } finally {
            setLoading(false);
          }
        }, 1000 * retryCountRef.current); // Exponential backoff
        return;
      }
      
      if (err.message === 'Request cancelled') {
        // Request was cancelled, don't show error to user
        console.log('Request was cancelled');
        return;
      }
      
      setError('Failed to load invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    retryCountRef.current = 0;
    fetchInvoice();
  };

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  return {
    invoice,
    loading,
    error,
    refetch
  };
};
